import { DeleteOutline, InsertDriveFile } from "@mui/icons-material";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemIcon,
	ListItemText, Typography } from "@mui/material";
import { FC, useState } from "react";
import { FinancialService } from "../../../services/FinancialService";
import { FinancialBillDTO } from "../../../types/FinancialDTO";
import { useAlert } from "../../contextProviders/AlertProvider";
import "./FinancialUploadModal.scss";

interface Props {
	bill: FinancialBillDTO | null;
	onClose: () => void;
	onUploaded: () => void;
}

const FinancialUploadModal: FC<Props> = ({ bill, onClose, onUploaded }) => {
	const [files, setFiles] = useState<File[]>([]);
	const [dragging, setDragging] = useState(false);
	const [saving, setSaving] = useState(false);
	const { showAlert, showError } = useAlert();
	const service = new FinancialService();

	const close = () => {
		setFiles([]);
		setDragging(false);
		onClose();
	};

	const add = (added: File[]) => {
		if (added.length) {
			setFiles(previous => [...previous, ...added]);
		}
	};

	const save = async () => {
		if (!bill) {
			return;
		}
		setSaving(true);
		const form = new FormData();
		files.forEach(file => form.append("files", file));
		const response = await service.uploadDocuments(bill.idConta, form);
		setSaving(false);
		if (!response) {
			showError('Não foi possível anexar os arquivos. Tente novamente.');
			return;
		}
		showAlert('Arquivos anexados com sucesso!');
		setFiles([]);
		onUploaded();
	};

	return (
		<Dialog open={!!bill} onClose={close} maxWidth="sm" fullWidth>
			<DialogTitle>Adicionar arquivo</DialogTitle>
			<DialogContent className="financial-upload">
				<Typography variant="body2">{bill?.companyName}</Typography>
				<Box
					className={dragging ? "drop-area dragging" : "drop-area"}
					onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
					onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
					onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
					onDrop={(e) => {
						e.preventDefault();
						setDragging(false);
						add(Array.from(e.dataTransfer.files));
					}}
				>
					<Typography>{dragging ? "Solte os arquivos aqui" : "Arraste e solte os arquivos aqui"}</Typography>
					<Button variant="contained" component="label">
						Escolher arquivos
						<input hidden type="file" multiple onChange={(e) => {
							if (e.target.files) {
								add(Array.from(e.target.files));
							}
							e.target.value = '';
						}}/>
					</Button>
				</Box>
				{!!files.length &&
					<List dense>
						{files.map((file, index) => (
							<ListItem key={`${file.name}-${index}`} secondaryAction={
								<IconButton edge="end" color="error" title="Remover arquivo"
									onClick={() => setFiles(files.filter((_, position) => position !== index))}>
									<DeleteOutline/>
								</IconButton>
							}>
								<ListItemIcon><InsertDriveFile color="action"/></ListItemIcon>
								<ListItemText primary={file.name} primaryTypographyProps={{ noWrap: true }}/>
							</ListItem>
						))}
					</List>
				}
			</DialogContent>
			<DialogActions>
				<Button onClick={close}>Cancelar</Button>
				<Button variant="contained" color="success" disabled={!files.length || saving} onClick={save}
					startIcon={saving ? <CircularProgress size={16}/> : undefined}>
					Confirmar
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default FinancialUploadModal
